import { useCallback, useEffect, useRef, useState } from "react";
import localStorageConstants from "src/app/constants/localStorage.constants";
import {
  FilesBreadcrumb,
  File,
  Folder,
  FielsStorageInfo,
} from "src/app/models/screens/files.model";
import { RequestManagementUser } from "src/app/models/screens/requestManagement.model";
import {
  addFilesAccess,
  createCloudieFolder,
  deleteFilesFile,
  deleteFilesFolder,
  downloadFilesFile,
  getCloudieItems,
  getCloudieMediaUrl,
  getParentChildren,
  removeFilesACcess,
  renameFilesFile,
  RenameFilesFolder,
  requestMoreCloudStorage,
  searchUsers,
  uploadCloudieFile,
} from "src/app/utils/backendEndpointCalls.util";

interface ParentChild {
  _id: string;
  firstName: string;
}

export interface UploadTask {
  id: string;
  fileName: string;
  progress: number;
  done: boolean;
  error?: string;
}

export const ACCEPTED_MEDIA_TYPES = "image/*,video/*";

const CloudieScreenViewModel = () => {
  const [userRole, setUserRole] = useState<string | undefined>(undefined);
  const [children, setChildren] = useState<ParentChild[]>([]);
  const [isLoadingChildren, setIsLoadingChildren] = useState(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>("you");
  const [adminUsers, setAdminUsers] = useState<RequestManagementUser[]>([]);
  const [isLoadingAdminUsers, setIsLoadingAdminUsers] = useState(false);

  const [isSnackbarError, setIsSnackbarError] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isDisplayingSnackbar, setIsDisplayingSnackbar] = useState(false);

  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<FilesBreadcrumb[]>([
    { id: null, name: "My Cloudie" },
  ]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [storageInfo, setStorageInfo] = useState<FielsStorageInfo | null>(null);

  const [uploadTasks, setUploadTasks] = useState<UploadTask[]>([]);
  const uploadCounterRef = useRef(0);

  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);

  const [renameTarget, setRenameTarget] = useState<{
    id: string;
    type: "folder" | "file";
    currentName: string;
  } | null>(null);
  const [renameName, setRenameName] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    type: "folder" | "file";
    name: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [accessTarget, setAccessTarget] = useState<{
    id: string;
    type: "folder" | "file";
    name: string;
    accessList: { userId: string; firstName: string; lastName: string }[];
  } | null>(null);
  const [accessUserSearchText, setAccessUserSearchText] = useState("");
  const [accessSearchResults, setAccessSearchResults] = useState<
    RequestManagementUser[]
  >([]);
  const [isSearchingAccessUsers, setIsSearchingAccessUsers] = useState(false);
  const [isModifyingAccess, setIsModifyingAccess] = useState(false);

  const [isStorageRequestOpen, setIsStorageRequestOpen] = useState(false);
  const [storageRequestGb, setStorageRequestGb] = useState<string>("");
  const [isSubmittingStorageRequest, setIsSubmittingStorageRequest] =
    useState(false);

  const [viewerFile, setViewerFile] = useState<File | null>(null);
  const [viewerIndex, setViewerIndex] = useState<number>(-1);

  const filesRef = useRef<File[]>([]);
  const viewerIndexRef = useRef<number>(-1);
  useEffect(() => {
    filesRef.current = files;
  }, [files]);
  useEffect(() => {
    viewerIndexRef.current = viewerIndex;
  }, [viewerIndex]);

  const getCurrentChildId = useCallback((): string | undefined => {
    if (userRole !== "Parent") return undefined;
    return selectedOwnerId === "you" ? undefined : selectedOwnerId;
  }, [selectedOwnerId, userRole]);

  const getAdminViewAsUserId = useCallback((): string | undefined => {
    if (userRole !== "Admin") return undefined;
    return selectedOwnerId === "you" ? undefined : selectedOwnerId;
  }, [selectedOwnerId, userRole]);

  const isViewingAsAdmin = userRole === "Admin" && selectedOwnerId !== "you";

  const storageUsedPct = (() => {
    if (!storageInfo || storageInfo.maxBytes === 0) return 0;
    return Math.min(
      100,
      Math.round((storageInfo.usedBytes / storageInfo.maxBytes) * 100),
    );
  })();

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
    return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
  };

  const mediaUrl = (file: File, thumbnail: boolean): string =>
    getCloudieMediaUrl(
      file.id,
      thumbnail,
      getCurrentChildId(),
      getAdminViewAsUserId(),
    );

  const showError = (msg: string) => {
    setIsSnackbarError(true);
    setSnackbarMessage(msg);
    setIsDisplayingSnackbar(true);
  };
  const showSuccess = (msg: string) => {
    setIsSnackbarError(false);
    setSnackbarMessage(msg);
    setIsDisplayingSnackbar(true);
  };

  useEffect(() => {
    const role = localStorage.getItem(localStorageConstants.ROLE);
    if (role) setUserRole(role);
  }, []);

  const loadChildren = useCallback(async () => {
    setIsLoadingChildren(true);
    const res = await getParentChildren();
    setIsLoadingChildren(false);
    if (!res.success) {
      showError(res.error);
      return;
    }
    setChildren(res.children ?? []);
  }, []);

  const loadAdminUsers = useCallback(async () => {
    setIsLoadingAdminUsers(true);
    const res = await searchUsers("");
    setIsLoadingAdminUsers(false);
    if (!res.success) {
      showError(res.error);
      return;
    }
    setAdminUsers(res.users ?? []);
  }, []);

  const loadItems = useCallback(async () => {
    setIsLoadingItems(true);
    const res = await getCloudieItems(
      currentFolderId,
      getCurrentChildId(),
      getAdminViewAsUserId(),
    );
    setIsLoadingItems(false);
    if (!res.success) {
      showError(res.error);
      return;
    }
    setFolders(res.folders ?? []);
    setFiles(res.files ?? []);
    if (res.storageInfo) setStorageInfo(res.storageInfo);
  }, [currentFolderId, getAdminViewAsUserId, getCurrentChildId]);

  useEffect(() => {
    if (!userRole) return;
    if (userRole === "Parent") loadChildren();
    if (userRole === "Admin") loadAdminUsers();
  }, [userRole, loadAdminUsers, loadChildren]);

  useEffect(() => {
    if (!userRole) return;
    loadItems();
  }, [userRole, currentFolderId, selectedOwnerId, loadItems]);

  useEffect(() => {
    if (!accessTarget || !accessUserSearchText.trim()) {
      setAccessSearchResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setIsSearchingAccessUsers(true);
      const res = await searchUsers(accessUserSearchText.trim());
      setIsSearchingAccessUsers(false);
      if (res.success) setAccessSearchResults(res.users ?? []);
    }, 300);
    return () => clearTimeout(t);
  }, [accessUserSearchText, accessTarget]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (viewerIndexRef.current === -1) return;
      if (e.key === "ArrowRight") stepViewer(1);
      if (e.key === "ArrowLeft") stepViewer(-1);
      if (e.key === "Escape") closeViewer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const stepViewer = (direction: 1 | -1) => {
    const list = filesRef.current;
    if (!list.length) return;
    const next =
      (viewerIndexRef.current + direction + list.length) % list.length;
    setViewerFile(list[next]);
    setViewerIndex(next);
  };

  const onFolderOpen = (folder: Folder) => {
    setCurrentFolderId(folder.id);
    setBreadcrumbs((prev) => [...prev, { id: folder.id, name: folder.name }]);
  };

  const onBreadcrumbClicked = (crumb: FilesBreadcrumb) => {
    setCurrentFolderId(crumb.id);
    setBreadcrumbs((prev) => {
      const idx = prev.findIndex((c) => c.id === crumb.id);
      return idx >= 0 ? prev.slice(0, idx + 1) : prev;
    });
  };

  const onOwnerChanged = (ownerId: string) => {
    setSelectedOwnerId(ownerId);
    setCurrentFolderId(null);
    setBreadcrumbs([{ id: null, name: "My Cloudie" }]);
    setFolders([]);
    setFiles([]);
  };

  const onFilesSelected = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;
    Array.from(selectedFiles).forEach((file) => {
      if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
        showError(`${file.name} is not a photo or video and was skipped.`);
        return;
      }
      const taskId = `upload-${++uploadCounterRef.current}`;
      setUploadTasks((prev) => [
        ...prev,
        { id: taskId, fileName: file.name, progress: 0, done: false },
      ]);
      uploadCloudieFile(file, currentFolderId, getCurrentChildId(), (pct) => {
        setUploadTasks((prev) =>
          prev.map((t) => (t.id === taskId ? { ...t, progress: pct } : t)),
        );
      }).then((res) => {
        if (res.success) {
          setUploadTasks((prev) =>
            prev.map((t) =>
              t.id === taskId ? { ...t, progress: 100, done: true } : t,
            ),
          );
          loadItems();
          setTimeout(() => {
            setUploadTasks((prev) => prev.filter((t) => t.id !== taskId));
          }, 3000);
        } else {
          setUploadTasks((prev) =>
            prev.map((t) =>
              t.id === taskId ? { ...t, done: true, error: res.error } : t,
            ),
          );
        }
      });
    });
  };

  const dismissUploadTask = (taskId: string) => {
    setUploadTasks((prev) => prev.filter((t) => t.id !== taskId));
  };

  const openCreateFolderDialog = () => {
    setNewFolderName("");
    setIsCreateFolderOpen(true);
  };
  const closeCreateFolderDialog = () => {
    setIsCreateFolderOpen(false);
    setNewFolderName("");
  };

  const onCreateFolderConfirmed = async () => {
    if (!newFolderName.trim()) {
      showError("Please enter a folder name.");
      return;
    }
    setIsCreatingFolder(true);
    const res = await createCloudieFolder(
      newFolderName.trim(),
      currentFolderId,
      getCurrentChildId(),
    );
    setIsCreatingFolder(false);
    if (!res.success) {
      showError(res.error);
      return;
    }
    closeCreateFolderDialog();
    showSuccess("Folder created.");
    loadItems();
  };

  const openRenameDialog = (
    id: string,
    type: "folder" | "file",
    currentName: string,
  ) => {
    setRenameTarget({ id, type, currentName });
    setRenameName(currentName);
  };
  const closeRenameDialog = () => {
    setRenameTarget(null);
    setRenameName("");
  };

  const onRenameConfirmed = async () => {
    if (!renameTarget || !renameName.trim()) {
      showError("Please enter a valid name.");
      return;
    }
    setIsRenaming(true);
    const res =
      renameTarget.type === "folder"
        ? await RenameFilesFolder(
            renameTarget.id,
            renameName.trim(),
            getCurrentChildId(),
          )
        : await renameFilesFile(
            renameTarget.id,
            renameName.trim(),
            getCurrentChildId(),
          );
    setIsRenaming(false);
    if (!res.success) {
      showError(res.error);
      return;
    }
    closeRenameDialog();
    showSuccess("Renamed successfully.");
    loadItems();
  };

  const openDeleteDialog = (
    id: string,
    type: "folder" | "file",
    name: string,
  ) => {
    setDeleteTarget({ id, type, name });
  };
  const closeDeleteDialog = () => setDeleteTarget(null);

  const onDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const res =
      deleteTarget.type === "folder"
        ? await deleteFilesFolder(deleteTarget.id, getCurrentChildId())
        : await deleteFilesFile(deleteTarget.id, getCurrentChildId());
    setIsDeleting(false);
    if (!res.success) {
      showError(res.error);
      return;
    }
    if (viewerFile?.id === deleteTarget.id) closeViewer();
    closeDeleteDialog();
    showSuccess("Deleted successfully.");
    loadItems();
  };

  const onDownloadFile = async (file: File) => {
    const res = await downloadFilesFile(
      file.id,
      file.name,
      getCurrentChildId(),
      getAdminViewAsUserId(),
    );
    if (!res.success) showError(res.error ?? "Download failed.");
  };

  const openAccessDialog = (item: Folder | File, type: "folder" | "file") => {
    setAccessTarget({
      id: item.id,
      type,
      name: item.name,
      accessList: item.accessList,
    });
    setAccessUserSearchText("");
    setAccessSearchResults([]);
  };
  const closeAccessDialog = () => {
    setAccessTarget(null);
    setAccessUserSearchText("");
    setAccessSearchResults([]);
  };

  const onAddAccess = async (targetUser: RequestManagementUser) => {
    if (!accessTarget) return;
    if (accessTarget.accessList.some((a) => a.userId === targetUser.id)) {
      showError("User already has access.");
      return;
    }
    setIsModifyingAccess(true);
    const res = await addFilesAccess(
      accessTarget.id,
      accessTarget.type,
      targetUser.id,
      getCurrentChildId(),
    );
    setIsModifyingAccess(false);
    if (!res.success) {
      showError(res.error);
      return;
    }
    setAccessTarget((prev) =>
      prev
        ? {
            ...prev,
            accessList: [
              ...prev.accessList,
              {
                userId: targetUser.id,
                firstName: targetUser.firstName,
                lastName: targetUser.lastName,
              },
            ],
          }
        : null,
    );
    setAccessUserSearchText("");
    setAccessSearchResults([]);
    showSuccess(`Access granted to ${targetUser.firstName}.`);
    loadItems();
  };

  const onRemoveAccess = async (userId: string) => {
    if (!accessTarget) return;
    setIsModifyingAccess(true);
    const res = await removeFilesACcess(
      accessTarget.id,
      accessTarget.type,
      userId,
      getCurrentChildId(),
    );
    setIsModifyingAccess(false);
    if (!res.success) {
      showError(res.error);
      return;
    }
    setAccessTarget((prev) =>
      prev
        ? {
            ...prev,
            accessList: prev.accessList.filter((a) => a.userId !== userId),
          }
        : null,
    );
    showSuccess("Access removed.");
    loadItems();
  };

  const openStorageRequestDialog = () => {
    setStorageRequestGb("");
    setIsStorageRequestOpen(true);
  };
  const closeStorageRequestDialog = () => {
    setIsStorageRequestOpen(false);
    setStorageRequestGb("");
  };

  const onStorageRequestConfirmed = async () => {
    const gb = parseFloat(storageRequestGb);
    if (isNaN(gb) || gb <= 0) {
      showError("Please enter a valid number of GBs.");
      return;
    }
    setIsSubmittingStorageRequest(true);
    const res = await requestMoreCloudStorage(gb, getCurrentChildId());
    setIsSubmittingStorageRequest(false);
    if (!res.success) {
      showError(res.error);
      return;
    }
    closeStorageRequestDialog();
    showSuccess(
      "Storage request submitted. An admin will review it in Cloudie.",
    );
  };

  const openViewer = (file: File, index: number) => {
    setViewerFile(file);
    setViewerIndex(index);
  };
  const closeViewer = () => {
    setViewerFile(null);
    setViewerIndex(-1);
  };
  const viewerNext = () => stepViewer(1);
  const viewerPrev = () => stepViewer(-1);

  return {
    userRole,
    children,
    isLoadingChildren,
    selectedOwnerId,
    onOwnerChanged,
    adminUsers,
    isLoadingAdminUsers,
    isViewingAsAdmin,
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    currentFolderId,
    breadcrumbs,
    folders,
    files,
    isLoadingItems,
    storageInfo,
    storageUsedPct,
    formatBytes,
    onFolderOpen,
    onBreadcrumbClicked,
    uploadTasks,
    onFilesSelected,
    dismissUploadTask,
    isCreateFolderOpen,
    newFolderName,
    setNewFolderName,
    isCreatingFolder,
    openCreateFolderDialog,
    closeCreateFolderDialog,
    onCreateFolderConfirmed,
    renameTarget,
    renameName,
    setRenameName,
    isRenaming,
    openRenameDialog,
    closeRenameDialog,
    onRenameConfirmed,
    deleteTarget,
    isDeleting,
    openDeleteDialog,
    closeDeleteDialog,
    onDeleteConfirmed,
    onDownloadFile,
    accessTarget,
    accessUserSearchText,
    setAccessUserSearchText,
    accessSearchResults,
    isSearchingAccessUsers,
    isModifyingAccess,
    openAccessDialog,
    closeAccessDialog,
    onAddAccess,
    onRemoveAccess,
    isStorageRequestOpen,
    storageRequestGb,
    setStorageRequestGb,
    isSubmittingStorageRequest,
    openStorageRequestDialog,
    closeStorageRequestDialog,
    onStorageRequestConfirmed,
    viewerFile,
    viewerIndex,
    openViewer,
    closeViewer,
    viewerNext,
    viewerPrev,
    mediaUrl,
  };
};

export default CloudieScreenViewModel;
